/**
 * Technical agreement on an offer (`docs/V2_OFFER_TECH_APPROVAL.md`, owner decision 2026-09-28,
 * Jira SCRUM-424) — the shared contract for a SUPPLIER's answer to a published rider.
 *
 * The shape, in one sentence: a supplier answers every line of a rider's input/output/gear lists
 * ("ok" / "substitute" / "band carries it" / "not available"), the rider's own author (the band's
 * sound engineer) gives the ONLY approval that exists in v1 — TECHNICAL — and an ORGANISER slot is
 * modelled now, always `"not_required"`, so an organisational/financial approval can be wired in
 * later without re-meaning the status derivation (there is no organiser role in the system yet).
 *
 * An offer never mutates the rider it answers — it is its own document, referencing a rider by
 * `(rider_id, rider_version)`, i.e. exactly one FROZEN `rider_publications` snapshot (see
 * `apps/api/src/riders/rider-publication.entity.ts`). This package has zero knowledge of
 * persistence; `apps/api/src/offers` owns the table and the endpoints.
 *
 * PR1 (this file): the contract + the API. No web UI ships in this PR — see
 * `docs/V2_OFFER_TECH_APPROVAL.md` for the four-PR slicing.
 */
import { z } from "zod";
import { UuidSchema } from "./schema";
import { offerSystems, systemAsks } from "./offer-system";
import { isCablingGear } from "./cabling";
// ---------------------------------------------------------------------------------------------
// Line refs — what a single offer line is ANSWERING. Deliberately not a uuid: list lines carry no
// uuid of their own (see `packages/schema/src/schema.ts`'s `owner_uuid` doc) and `Channel` is
// unique per list across the whole rider tree (`validate.ts`'s semantic checks), so an input/
// output line is addressed by its list + Channel. A gear line has neither a uuid nor a channel —
// the viewer already keys the SUMMED gear order by `"category|owner|name"`
// (`apps/web/src/editor/gear-summary.ts`), so an offer answers that same merged line, not any one
// object's raw row. `gearOrderLineKey()` below is the single place that key is computed, shared by
// the web viewer and this package so the two sides can never drift.
// ---------------------------------------------------------------------------------------------
export const OFFER_LINE_LISTS = ["input", "output", "gear", "system", "cabling"];
export const OfferLineRefSchema = z.discriminatedUnion("list", [
    z.object({ list: z.literal("input"), channel: z.number().int().min(1) }).strict(),
    z.object({ list: z.literal("output"), channel: z.number().int().min(1) }).strict(),
    z.object({ list: z.literal("gear"), key: z.string().min(1).max(400) }).strict(),
    /**
     * A MIXING SYSTEM the rider ASKS for (`Rider.Mixing_system`, SCRUM-866, owner 27-28.10.2026): «прокатчик предлагает подходящий
     * стейджбокс или подтверждает конкретный если запрошен». A slot (FOH, or the separate monitor desk) has TWO questions - the desk
     * (`console`) and its stage box (`stage_box`). The picks a slot lists are ALTERNATIVES (owner, 28.10.2026: «Если пульты
     * перечислены как варианты то прокатчик предлагает один из вариантов»), so the ref names the slot, not a pick; FOH and a separate
     * monitor desk are two independent questions. The answer is the ordinary one: `ok` confirms, `substitute` names the model the
     * rental brings (`model` + `model_ids`). On a `system` ref `model` + `model_ids` are also allowed on `ok`: they NAME the variant
     * the rental offers (or the stage box it brings) - a variant of the rider's own list is no deviation.
     */
    z
        .object({
        list: z.literal("system"),
        slot: z.enum(["foh", "monitor"]),
        part: z.enum(["console", "stage_box"]),
    })
        .strict(),
    /**
     * ALL CABLING TO CONNECT THE EQUIPMENT (SCRUM-861, contract 2.21.0; owner, 27.10.2026: «Все кабели убираем; в оффере должно быть
     * "вся коммутация для подключения оборудования"»). ONE line for every cable, power cord and AC adapter of the rider (`isCablingGear`):
     * the rental answers it once, and the cables are no longer answered one by one («вся эта мелкая информация она по сути для машин у
     * нас»). The ref names no cable - it has no fields. Its answer drives the whole composition: the crew drops the cabling when the
     * rental says it does not bring it, and it reserves NOTHING in the warehouse (a shortage of cables is the pick list's business).
     */
    z.object({ list: z.literal("cabling") }).strict(),
]);
/** A stable string identity for a ref, used only for de-duplication within one draft. */
export function offerLineRefKey(ref) {
    if (ref.list === "gear")
        return `gear|${ref.key}`;
    if (ref.list === "system")
        return `system|${ref.slot}|${ref.part}`;
    if (ref.list === "cabling")
        return "cabling";
    return `${ref.list}|${ref.channel}`;
}
/**
 * The SAME merge key `apps/web/src/editor/gear-summary.ts#summarizeGearList` computes for its
 * `lineKey` (category/owner/name, case-insensitive) — moved here so the web viewer and this
 * package's `OfferLineRef` for `list: "gear"` can never disagree about what identifies a summed
 * gear line. `gear-summary.ts` calls this function rather than re-deriving the string itself.
 */
export function gearOrderLineKey(category, owner, name) {
    return `${category.trim().toLowerCase()}|${owner.trim().toLowerCase()}|${name.trim().toLowerCase()}`;
}
// ---------------------------------------------------------------------------------------------
// The answer to one line, and the whole draft.
// ---------------------------------------------------------------------------------------------
/**
 * `ok` — the supplier provides exactly what was asked.
 * `substitute` — a different model than asked, named in `model`.
 * `band_carried` — the band brings this themselves; nothing for the supplier to provide.
 * `na` — not available; `comment` must say why/what the band should expect instead.
 * `venue_provided` — the VENUE has it (house PA, risers, console…): covered, not a refusal; an
 *   optional `comment` says what exactly stands there (SCRUM-493, owner 01.10.2026).
 */
export const OFFER_ANSWERS = ["ok", "substitute", "band_carried", "na", "venue_provided"];
export const OfferAnswerSchema = z.enum(OFFER_ANSWERS);
const MODEL_MAX_LENGTH = 255;
const LINE_COMMENT_MAX_LENGTH = 2000;
export const OfferLineSchema = z
    .object({
    ref: OfferLineRefSchema,
    answer: OfferAnswerSchema,
    model: z.string().min(1).max(MODEL_MAX_LENGTH).optional(),
    /**
     * The catalogue link of a `substitute` (SCRUM-610, contract 2.14.0, rule 43: a deviation is a
     * reference to an item, not only a string). Same shape and rules as the rider's `equipment_ids`:
     * `equipment.id`s in OUR catalogue, distinct, ordered, the model offered first. `model` stays the
     * words as typed or picked; the offers API refuses ids that are not in the catalogue (400 naming them) when a draft is saved; this schema itself does not consult the catalogue.
     * Only a `substitute` carries one.
     */
    model_ids: z
        .array(z.number().int().positive())
        .max(6)
        .refine((ids) => new Set(ids).size === ids.length, { message: "the models in one list must be distinct" })
        .optional(),
    comment: z.string().max(LINE_COMMENT_MAX_LENGTH).optional(),
})
    .strict()
    .superRefine((line, ctx) => {
    if (line.answer === "substitute" && !line.model?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["model"],
            message: "a 'substitute' answer requires a non-empty model",
        });
    }
    // On a `system` ref the model also rides on «ok»: it names the variant of the rider's own list that the rental offers.
    if (line.model_ids !== undefined && line.answer !== "substitute" && !(line.ref.list === "system" && line.answer === "ok")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["model_ids"],
            message: "model_ids is only allowed on a 'substitute' answer",
        });
    }
    if (line.answer === "na" && !line.comment?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["comment"],
            message: "an 'na' answer requires a non-empty comment",
        });
    }
});
/** Sanity ceiling, not a real-world cap — the largest sampled rider carries far fewer lines. */
export const MAX_OFFER_LINES = 2000;
export const MAX_OFFER_COMMENT_LENGTH = 4000;
export const OfferDraftSchema = z
    .object({
    lines: z.array(OfferLineSchema).max(MAX_OFFER_LINES),
    comment: z.string().max(MAX_OFFER_COMMENT_LENGTH),
    /**
     * The lines the supplier LEFT at «—» on purpose (SCRUM-839, owner 27.10.2026: an offer "opens as the person last saw it").
     * An unanswered line is simply absent from `lines`, so without this the page cannot tell «never touched» (the auto-offer
     * may fill it) from «set back to —» (it must stay empty). Never a line that is also answered; it answers nothing and is
     * no part of what is sent to the rider's author.
     */
    left_blank: z.array(OfferLineRefSchema).max(MAX_OFFER_LINES).optional(),
})
    .strict()
    .superRefine((draft, ctx) => {
    const seen = new Map();
    draft.lines.forEach((line, index) => {
        const refKey = offerLineRefKey(line.ref);
        const firstIndex = seen.get(refKey);
        if (firstIndex !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lines", index, "ref"],
                message: `duplicate ref (${refKey}, already used at lines[${firstIndex}]) — each line may answer a ref at most once`,
            });
        }
        else {
            seen.set(refKey, index);
        }
    });
    const blankSeen = new Set();
    (draft.left_blank ?? []).forEach((ref, index) => {
        const refKey = offerLineRefKey(ref);
        if (seen.has(refKey) || blankSeen.has(refKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["left_blank", index],
                message: `${refKey} is listed twice, or is both answered and left blank`,
            });
        }
        blankSeen.add(refKey);
    });
});
/** Structural entry point, mirroring `validateRider()`/`validateOps()` — never throws. */
export function validateOfferDraft(input) {
    let candidate = input;
    if (typeof input === "string") {
        try {
            candidate = JSON.parse(input);
        }
        catch (err) {
            return { success: false, errors: [{ path: "$", message: `malformed JSON: ${err.message}` }] };
        }
    }
    const parsed = OfferDraftSchema.safeParse(candidate);
    if (!parsed.success) {
        const errors = parsed.error.issues.map((issue) => ({
            path: issue.path.join("."),
            message: issue.message,
        }));
        return { success: false, errors };
    }
    return { success: true, data: parsed.data, warnings: [] };
}
// ---------------------------------------------------------------------------------------------
// Approval slots. TWO of them, and they are NOT symmetric.
//
// `technical` is given by the rider's own author (the band's sound engineer) and is, in v1, the
// ONLY approval that exists — see `deriveOfferStatus` below.
//
// `organiser` is an explicit PLACEHOLDER (owner decision 2026-09-28, Jira SCRUM-424: "there is no
// organiser in the system yet. Leave an explicit organiser PLACEHOLDER so an organisational/
// financial approval can plug in later without re-meaning anything"). Its state is ALWAYS
// `"not_required"` in v1 and is written by NOBODY — `apps/api/src/offers` never sets it to
// anything else. It exists here, fully modelled, so a later PR only has to start WRITING it; the
// derivation rule below already knows what every other state means.
// ---------------------------------------------------------------------------------------------
export const TECHNICAL_APPROVAL_STATES = ["pending", "approved", "changes_requested", "declined"];
export const ORGANISER_APPROVAL_STATES = ["not_required", "pending", "approved", "changes_requested"];
/** The v1 default — nobody but the API's own offer-creation path ever writes this object. */
export const NOT_REQUIRED_ORGANISER_APPROVAL = { state: "not_required" };
export const OFFER_STATUSES = ["draft", "submitted", "changes_requested", "awaiting_organiser", "approved", "declined", "withdrawn"];
/**
 * Why the rental company withdrew its own offer (SCRUM-865, owner 27.10.2026): one tap, and none is also fine. `price` — «по
 * деньгам с устроителем не договорились», `dates` — not available on these dates, `other` — anything else. The reason is
 * the rental's own word to the band; the system only counts it.
 */
export const OFFER_WITHDRAW_REASONS = ["price", "dates", "other"];
function isSubmitted(input) {
    if (typeof input.round === "number")
        return input.round > 0;
    return input.submittedAt !== null && input.submittedAt !== undefined;
}
/**
 * Pure status derivation — the SAME rule the API recomputes into its `status` cache column on
 * every write that can change it (submit / request-changes / approve). Order is deliberate:
 *
 *   0. the rental withdrew it (SCRUM-865)                       -> withdrawn
 *   1. not yet submitted                                       -> draft
 *   1b. technical declined (author's final no, SCRUM-587)       -> declined
 *   2. either side changes_requested                            -> changes_requested
 *   3. technical still pending                                   -> submitted
 *   4. technical approved AND organiser not_required/approved   -> approved
 *   5. otherwise (technical approved, organiser pending)         -> awaiting_organiser
 *
 * Step 5 is the whole point of the organiser placeholder: it is UNREACHABLE in v1 (nothing ever
 * sets `organiser.state` to `"pending"`), and it proves the derivation is already correct for the
 * day something does.
 */
export function deriveOfferStatus(input) {
    // The rental's own "no" outranks everything, an approved offer included («по деньгам не договорились»): the words of the
    // other two parties stay on the record, but the offer is no longer anyone's to decide.
    if (input.withdrawn)
        return "withdrawn";
    if (!isSubmitted(input))
        return "draft";
    // The author's final "no" (SCRUM-587, owner decision 30) outranks everything else, the organiser slot included.
    if (input.technical.state === "declined")
        return "declined";
    if (input.technical.state === "changes_requested" || input.organiser.state === "changes_requested") {
        return "changes_requested";
    }
    if (input.technical.state === "pending")
        return "submitted";
    // technical.state === "approved" from here on.
    if (input.organiser.state === "not_required" || input.organiser.state === "approved") {
        return "approved";
    }
    return "awaiting_organiser";
}
/**
 * ЧЕМ НАЗВАТЬ ЛИНИЮ В ОТВЕТЕ — одна функция на всех, кто создаёт или ищет ответ (SCRUM-564).
 *
 * Номер линии, когда он есть; иначе текстовый ключ. Разные места не должны решать это по-разному:
 * одно построит ссылку по номеру, другое будет искать по тексту — и ответ «потеряется», хотя лежит
 * рядом. До 2.16.0 ключ был только текстовым, и ответы, выданные тогда, находятся по нему же.
 */
export function gearLineRefKey(lineId, category, owner, name) {
    return lineId || gearOrderLineKey(category, owner, name);
}
/**
 * Чем строка гира может быть названа в ответе прокатчика: номером линии (с 2.16.0) И текстовым
 * ключом. ОБА, а не один: ответы, выданные до 2.16.0, привязаны к тексту, и терять их нельзя —
 * поэтому номер ДОБАВЛЯЕТСЯ, а не заменяет (SCRUM-564).
 */
function gearRefsOf(item) {
    const key = gearOrderLineKey(item.Category, item.Owner, item.Name);
    return item.line_id ? [item.line_id, key] : [key];
}
function addGear(item, out) {
    for (const ref of gearRefsOf(item))
        out.gearKeys.add(ref);
    if (isCablingGear(item.Category, item.Name))
        out.cabling = true;
}
function collectFromChildNode(node, out) {
    for (const item of node.input_list)
        out.inputChannels.add(item.Channel);
    for (const item of node.output_list)
        out.outputChannels.add(item.Channel);
    for (const item of node.gear_list)
        addGear(item, out);
    for (const child of node.children)
        collectFromChildNode(child, out);
    for (const set of node.sets ?? [])
        collectFromSetNode(set, out);
}
function collectFromSetNode(set, out) {
    for (const child of set.children)
        collectFromChildNode(child, out);
    for (const nested of set.sets ?? [])
        collectFromSetNode(nested, out);
}
/** Every Channel/gear-key a rider document actually carries, anywhere in its tree (root + every
 *  descendant, real objects AND objects living inside a Set). */
export function collectOfferableRefs(rider) {
    const out = { inputChannels: new Set(), outputChannels: new Set(), gearKeys: new Set(), systemKeys: new Set(), cabling: false };
    collectSystemRefs(rider, out);
    for (const item of rider.input_list)
        out.inputChannels.add(item.Channel);
    for (const item of rider.output_list)
        out.outputChannels.add(item.Channel);
    for (const item of rider.gear_list)
        addGear(item, out);
    for (const child of rider.children)
        collectFromChildNode(child, out);
    for (const set of rider.sets ?? [])
        collectFromSetNode(set, out);
    return out;
}
/**
 * The questions the rider asks about its mixing system, as `system` refs: per slot only the parts `offerSystems` still asks - a desk its
 * gear list already links, or a box it already names, is that gear line's question, and an answer to it here would reserve it twice.
 */
function collectSystemRefs(rider, out) {
    for (const system of offerSystems(rider)) {
        for (const part of ["console", "stage_box"]) {
            if (systemAsks(system, part))
                out.systemKeys.add(offerLineRefKey({ list: "system", slot: system.slot, part }));
        }
    }
}
function refExists(ref, available) {
    if (ref.list === "input")
        return available.inputChannels.has(ref.channel);
    if (ref.list === "output")
        return available.outputChannels.has(ref.channel);
    if (ref.list === "system")
        return available.systemKeys.has(offerLineRefKey(ref));
    if (ref.list === "cabling")
        return available.cabling;
    return available.gearKeys.has(ref.key);
}
/** True iff every ref in `refs` exists in `available` — the `PUT /offers/:id` 400 check. */
export function offerRefsExist(refs, available) {
    return refs.every((ref) => refExists(ref, available));
}
/** Every ref NOT present in `available` — used to build a helpful 400 error body. */
export function findMissingOfferRefs(refs, available) {
    return refs.filter((ref) => !refExists(ref, available));
}
// ---------------------------------------------------------------------------------------------
// Admin self-offer (SCRUM-433, `docs/V2_OFFER_TECH_APPROVAL.md` "Admin self-offer"). An ADMIN may
// answer their OWN rider so one person can run the whole approval loop in development. Neither fact
// below is stored: both are COMPUTED from ids the offer and rider already carry, so there is no
// column, no migration, and nothing that can drift out of step with the rows.
// ---------------------------------------------------------------------------------------------
/** `selfOffer` — the supplier of the offer is the owner (author) of the rider it answers. A rider
 *  with no owner (a legacy/import row) is never a self-offer. */
export function isSelfOffer(supplierUserId, riderOwnerUserId) {
    return riderOwnerUserId != null && supplierUserId === riderOwnerUserId;
}
/** `alsoAuthor` — on a self-offer the caller's `role` stays `"supplier"`, and once the offer has
 *  been SUBMITTED they also hold the author's seat (approve / request changes). Before submission
 *  there is nothing to review, so it is false. */
export function offerAlsoAuthor(selfOffer, submittedAt) {
    return selfOffer && submittedAt != null;
}
/** `OFFER_SCHEMA_VERSION`, distinct from `SCHEMA_CONTRACT_VERSION` (index.ts) — versions the OFFER
 *  document shape specifically (`offers.schema_version` column), same pattern as a rider's own
 *  `schema_version` field being separate from the package's contract version. */
export const OFFER_SCHEMA_VERSION = 1;
// Re-exported so a consumer that only imports offer.ts still has the uuid validator it needs for
// `technical.by` / `organiser.userId`.
export { UuidSchema };
//# sourceMappingURL=offer.js.map